<template>
  <FormCard>
    <h2 class="font-bold mb-4">Period and budget</h2>
    <ElForm
      ref="form"
      v-loading="loading"
      :model="form"
      :rules="rules"
      :disabled="isError || loading || isSaving"
      label-position="top"
      label-width="100px"
    >
      <ElFormItem prop="name" :error="errors.name">
        <span slot="label"> Flight name </span>
        <ElInput v-model="form.name" :maxlength="50" minlength="3" show-word-limit />
      </ElFormItem>
      <ElFormItem prop="gam_unit" :error="errors.gam_unit">
        <span slot="label"> Sites </span>
        <el-select class="w-full" v-model="selectedIds" multiple filterable placeholder="Select sites">
          <el-option v-for="item in gamAdUnits" :key="item.value" :label="item.name" :value="item.id"> </el-option>
        </el-select>
      </ElFormItem>
      <!-- start/end -->
      <div class="flex items-center justify-between gap-0.5">
        <ElFormItem class="w-full" prop="startDate" :error="errors.startDate">
          <span slot="label"> Start Date </span>
          <el-date-picker
            class="w-full"
            v-model="form.startDate"
            format="d MMMM yyyy"
            value-format="yyyy-MM-dd"
            :picker-options="pickerStartOptions"
            type="date"
            placeholder="Start Date"
          >
          </el-date-picker>
        </ElFormItem>
        <ElFormItem class="w-full" prop="endDate" :error="errors.endDate">
          <span slot="label"> End Date </span>
          <el-date-picker
            class="w-full"
            v-model="form.endDate"
            :picker-options="pickerEndOptions"
            format="d MMMM yyyy"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder="Start Date"
            :disabled="!form.startDate"
          >
          </el-date-picker>
        </ElFormItem>
      </div>
      <!-- end -->
      <!-- budget -->
      <ElFormItem prop="budget" :error="errors.budget">
        <span slot="label"> Budget </span>
        <ElInput
          v-model="form.budget"
          @blur="impresionsCalc"
          type="number"
          placeholder="Enter campaign budget"
          class="no-number-appearance"
        >
          <template slot="prepend">&euro;</template>
        </ElInput>
      </ElFormItem>

      <div class="flex items-center justify-between p-3 mb-3.5 border border-primary_light rounded-md">
        <ElFormItem class="w-3/4 m-0">
          <span slot="label"> Impressions </span>
          <p class="m-0">
            Enter a budget to see how many impressions your campaign could reach. The more you invest, the greater the potential reach.
          </p>
        </ElFormItem>
        <div class="flex items-center gap-1">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.5 8C0.5 8 3.5 2.5 8 2.5C12.5 2.5 15.5 8 15.5 8C15.5 8 12.5 13.5 8 13.5C3.5 13.5 0.5 8 0.5 8Z"
              stroke="#C0C4CC"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 10.5C9.38071 10.5 10.5 9.38071 10.5 8C10.5 6.61929 9.38071 5.5 8 5.5C6.61929 5.5 5.5 6.61929 5.5 8C5.5 9.38071 6.61929 10.5 8 10.5Z"
              stroke="#C0C4CC"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="font-bold text-24px">{{ form.impresions === 0 ? '-' : form.impresions }}</span>
        </div>
      </div>
      <ElFormItem class="last:mb-0">
        <ElButton type="primary" class="w-full" :loading="isSaving" @click="submitForm">{{ ctaActionText }}</ElButton>
      </ElFormItem>
    </ElForm>
  </FormCard>
</template>
<script>
import { evaluate } from 'mathjs'
import cloneDeep from 'lodash/cloneDeep'
import Vue from 'vue'
import { DatePicker, OptionGroup } from 'element-ui'
import { mapGetters } from 'vuex'
import moment from 'moment'

import FormCard from '@/components/social-products/social-boost/parts/FormCard.vue'
import { notifyMessage } from '@/mixins/social-products/CampaignAutosave'
import SocialBoostCampaignMixin from '@/mixins/social-products/social-boost-campaign'

Vue.use(DatePicker)
Vue.use(OptionGroup)

export default {
  name: 'PeriodBudget',
  mixins: [SocialBoostCampaignMixin],
  components: {
    FormCard,
  },
  props: {
    type: {
      type: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isError: false,
      isValidForAutosave: false,
      form: {
        name: '',
        startDate: '',
        endDate: '',
        budget: '',
        impresions: 0,
        gam_unit: [],
      },
      errors: {
        name: '',
        startDate: '',
        endDate: '',
        budget: '',
        gam_unit: '',
      },
      rules: {
        name: [{ required: true, message: 'Please input a flight name', trigger: 'blur' }],
        startDate: [{ required: true, message: 'Please select a start date', trigger: 'change' }],
        endDate: [{ required: true, message: 'Please select a end date', trigger: 'change' }],
        gam_unit: [{ required: true, message: 'Please select a site', trigger: 'change' }],
        budget: [
          {
            validator: this.validateBudget,
            trigger: 'blur',
          },
        ],
      },
      pickerStartOptions: {
        disabledDate: this.disabledDates,
      },
      pickerEndOptions: { disabledDate: this.disabledEndDate },
    }
  },
  async beforeDestroy() {
    if (this.$route.query?.step === undefined && this.$route.query.campaign && this.isValidForAutosave) {
      this.$notify.info(notifyMessage)
      await this.saveData(false)
    }
  },
  async created() {
    if (this.$route?.query.campaign) {
      await this.updateStoreFormDetails(false)
    }
    if (this.$route?.params?.type !== 'simple') {
      this.rules.name = [
        {
          required: true,
          message: 'Please input a flight name',
          trigger: 'blur',
        },
        {
          required: true,
          min: 3,
          max: 50,
          message: 'Flight name must be between 3 and 50 characters',
          trigger: 'blur',
        },
        {
          validator: (rule, value, callback) => {
            if (!value || !value.trim()) {
              callback(new Error('Flight name cannot be just spaces.'))
            } else {
              callback()
            }
          },
          trigger: 'blur',
        },
      ]
    }
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaignForm', 'campaign', 'flight', 'adUnits']),
    ctaActionText() {
      switch (this.$route.name) {
        case 'social-boost-edit-flight':
          return 'Save changes'
        default:
          return 'Next'
      }
    },
    campaignType() {
      return this.$route.params.type
    },
    isSimpleCamp() {
      return this.$route.params.type === 'simple'
    },
    gamAdUnits() {
      const allOptions = this.adUnits?.flatMap((group) => group.options)
      return allOptions.sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
        return 0
      })
    },
  },
  watch: {
    campaign: {
      async handler(newValue) {
        const campaignData = cloneDeep(newValue)

        if (this.$route.name === 'social-boost-edit' || this.$route?.query?.flight || this.flight) {
          const flight = campaignData?.flights?.length === 1 ? campaignData?.flights[0] : null

          if (flight) {
            this.form.startDate = flight.start_date
            this.form.endDate = flight.end_date
            this.form.budget = flight.budget
            this.form.name = flight.name
            this.form.gam_unit = flight.gam_unit

            this.impresionsCalc()
          }
        }
      },
      immediate: true,
    },
    flight(flight) {
      if (this.$route.name === 'social-boost-edit-flight' || flight) {
        this.form.startDate = flight.start_date
        this.form.endDate = flight.end_date
        this.form.budget = flight.budget
        this.form.name = flight.name
        this.form.gam_unit = flight.gam_unit

        this.impresionsCalc()
      }
    },
    form: {
      handler(newValue) {
        this.$emit('change', { period: newValue })
        this.isValidForAutosave = newValue.startDate !== '' && newValue.endDate !== ''
      },
      deep: true,
    },
  },
  methods: {
    disabledDates(time) {
      return time.getTime() < moment().startOf('day').valueOf()
    },
    disabledEndDate(time) {
      if (!this.form.startDate) return false //Nu dezactiva daca startDate nu e selectat
      return time.getTime() <= moment(this.form.startDate).startOf('day').valueOf() // Dezactiveaza zilele inainte de startDate
    },
    validateBudget(rule, value, callback) {
      if (value === '' || value === null || value === undefined) {
        return callback()
      }
      const n = Number(value)
      if (Number.isNaN(n) || n < 0) {
        return callback(new Error('Please enter a valid budget'))
      }
      callback()
    },
    impresionsCalc() {
      if (!this.form.budget || this.form.budget === 0) {
        this.form.impresions = 0
        return
      }

      const formula = this.campaignForm?.details?.client?.formula || '(budget / cpm) * 1000'
      const cpm = this.campaignForm?.details?.client?.CPM

      if (!cpm) {
        this.form.impresions = 0
        return
      }

      try {
        const impresions = evaluate(formula, {
          budget: Number(this.form.budget),
          cpm: Number(cpm),
        })

        var formatted = this.$numbro(impresions).format({
          average: true,
          thousandSeparated: true,
          mantissa: 1,
          roundingFunction: Math.floor,
        })

        formatted = formatted.replace(/([a-z])$/, (m) => m.toUpperCase())
        this.form.impresions = formatted
      } catch (error) {
        console.error('Error calculating impressions:', error)
        this.form.impresions = 0
      }
    },
    async saveData(activeSteps = true) {
      // if (this.isSimpleCamp) {
      //   this.form.name = this.$period(this.form.startDate, this.form.endDate)
      // }

      this.$store.commit('socialBoostCampaign/SET_CAMPAIGN_FORM', { section: 'periodBudget', data: this.form })

      if (this.$route.name === 'social-boost-edit-flight' || this.flight) {
        const res = await this.$store.dispatch('socialBoostCampaign/saveFlightForm')
        this.isSaving = false
        if (res) {
          if (res?.campaign?.is_simple) {
            if (activeSteps) {
              this.$emit('step', 2)
              if (res?.campaign?.flights[0].id && this.isSimpleCamp && activeSteps) {
                this.$router.push({
                  query: {
                    ...this.$route.query,
                    flight: res?.campaign?.flights[0].id,
                    step: 2,
                  },
                })
                this.isSaving = false
              }
            }
          } else {
            this.$router.push({
              name: 'view-campaign',
              params: { id: this.$route.params.id },
            })
          }
        }
      } else {
        if (this.$route.query.campaign) {
          const res = await this.$store.dispatch('socialBoostCampaign/saveFlightForm', {
            method: 'create',
            campaign: this.$route.query.campaign,
          })
          if (res?.campaign?.flights[0].id && this.isSimpleCamp && activeSteps) {
            this.$router.push({
              query: {
                ...this.$route.query,
                flight: res?.campaign?.flights[0].id,
                step: 2,
              },
            })
            this.isSaving = false
          }
        }
        if (activeSteps) {
          this.$emit('step', 2)
        }
      }
    },
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        this.$emit('validateForm', { step: 1, valid })
        if (valid) {
          this.isSaving = true
          await this.saveData()
        } else {
          // this.isError = true
        }
      })
    },
  },
}
</script>
